import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, Building2, X, UserCog, Info, ArrowLeftRight } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function Branches() {
  const { t } = useT();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", admin_id: "" });
  const [moveAm, setMoveAm] = useState("");
  const [moveSup, setMoveSup] = useState("");
  const [moveSupOptions, setMoveSupOptions] = useState([]);
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveMsg, setMoveMsg] = useState(null);
  const [moveConfirm, setMoveConfirm] = useState(false);

  useEffect(() => {
    if (!moveAm) { setMoveSupOptions([]); return; }
    api.get(`/admins/by-area/${moveAm}`).then((r) => setMoveSupOptions(r.data)).catch(() => setMoveSupOptions([]));
  }, [moveAm]);

  const adminMap = Object.fromEntries(admins.map((a) => [a.id, a.name]));

  const load = async () => {
    setLoading(true);
    try {
      const [br, ad] = await Promise.all([api.get("/branches"), api.get("/admins")]);
      setItems(br.data);
      setAdmins(ad.data);
      if (user?.role === "super_admin" || user?.role === "area_manager") {
        try { const am = await api.get("/area-managers/peers"); setAreaManagers(am.data); } catch { /* noop */ }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({}); setForm({ name: "", code: "", admin_id: "", branch_type: "" }); setSaveError(""); setMoveAm(""); setMoveSup(""); setMoveMsg(null); setMoveConfirm(false); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, code: b.code || "", admin_id: b.admin_id || "", branch_type: b.branch_type || "" }); setSaveError(""); setMoveAm(""); setMoveSup(""); setMoveMsg(null); setMoveConfirm(false); };
  const close = () => { setEditing(null); setSaveError(""); };

  const moveArea = async () => {
    if (!moveAm) return;
    setMoveBusy(true);
    setMoveMsg(null);
    try {
      const res = await api.post(`/branches/${editing.id}/move-area`, { area_manager_id: moveAm, new_admin_id: moveSup || null });
      setMoveMsg({ ok: true, text: t("move_branch_area_success").replace("{count}", res.data.moved_employees) });
      setMoveConfirm(false);
      setTimeout(() => { close(); load(); }, 1500);
    } catch (e) {
      const d = e.response?.data?.detail;
      setMoveMsg({ ok: false, text: typeof d === "string" ? d : t("save_failed") });
      setMoveConfirm(false);
    } finally { setMoveBusy(false); }
  };

  const [saveError, setSaveError] = useState("");
  const save = async () => {
    if (!form.name.trim()) return;
    if (moveAm) {
      setSaveError(t("move_branch_area_pending_save_warn"));
      return;
    }
    setSaveError("");
    const payload = { ...form, admin_id: form.admin_id || null, branch_type: form.branch_type || null };
    try {
      if (editing.id) {
        await api.put(`/branches/${editing.id}`, payload);
      } else {
        await api.post("/branches", payload);
      }
      close();
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && typeof d === "object") {
        if (d.message === "duplicate_branch_code") {
          setSaveError(t("duplicate_branch_code_error").replace("{code}", d.code || "").replace("{area_manager_name}", d.area_manager_name || "—"));
        } else if (d.message === "duplicate_branch_name") {
          setSaveError(t("duplicate_branch_name_error").replace("{name}", d.name || "").replace("{area_manager_name}", d.area_manager_name || "—"));
        } else {
          setSaveError(typeof d === "string" ? d : t("save_failed"));
        }
      } else {
        setSaveError(typeof d === "string" ? d : t("save_failed"));
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("delete_branch_confirm"))) return;
    await api.delete(`/branches/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="branches-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-teal-900">{t("nav_branches")}</h2>
          <p className="text-gray-500 text-sm">{t("branches_subtitle")}</p>
        </div>
        <button className="btn btn-primary" onClick={openNew} data-testid="add-branch-button">
          <Plus size={16} /> {t("add_branch")}
        </button>
      </div>

      <div className="bg-white rounded-2xl card-shadow overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">{t("loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-400">
            <Building2 size={36} className="mx-auto mb-3 opacity-50" />
            {t("no_branches")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("code_col")}</th>
                  <th>{t("name")}</th>
                  <th>{t("branch_admin")}</th>
                  <th className="text-left">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} data-testid={`branch-row-${b.id}`} className={b.is_vacation ? "bg-amber-50/40" : ""}>
                    <td className="text-gray-500">{b.code || "—"}</td>
                    <td className="font-semibold">
                      {b.is_vacation ? <span>🏖️ {b.name}</span> : b.name}
                    </td>
                    <td>
                      {b.is_vacation ? (
                        <span className="text-gray-400 text-xs">—</span>
                      ) : b.admin_id ? (
                        <span className="inline-flex items-center gap-1 text-teal-700 font-medium text-sm">
                          <UserCog size={13} /> {adminMap[b.admin_id] || "—"}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">{t("no_admin_set")}</span>
                      )}
                    </td>
                    <td className="text-left">
                      {b.is_vacation ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <>
                          <button className="btn btn-ghost" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`}>
                            <Edit3 size={14} /> {t("edit")}
                          </button>
                          <button className="btn btn-danger mr-2" onClick={() => remove(b.id)} data-testid={`delete-branch-${b.id}`}>
                            <Trash2 size={14} /> {t("delete")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in" onClick={close}>
          <div className="bg-white rounded-2xl card-shadow w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-teal-900">{editing.id ? t("edit_branch") : t("add_new_branch")}</h3>
              <button className="btn btn-ghost" onClick={close}><X size={16} /></button>
            </div>
            <div className="space-y-4">
              {saveError && (
                <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-2.5 text-sm" data-testid="branch-save-error">
                  ⚠️ {saveError}
                </div>
              )}
              <div>
                <label className="inp-label">{t("branch_name")}</label>
                <input
                  className="inp"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("branch_name_placeholder")}
                  autoFocus
                  data-testid="branch-name-input"
                />
              </div>
              <div>
                <label className="inp-label">{t("branch_code")}</label>
                <input
                  className="inp"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder={t("branch_code_placeholder")}
                  data-testid="branch-code-input"
                />
              </div>
              <div>
                <label className="inp-label">{t("branch_type_label")}</label>
                <select
                  className="inp"
                  value={form.branch_type}
                  onChange={(e) => setForm({ ...form, branch_type: e.target.value })}
                  data-testid="branch-type-select"
                >
                  <option value="">—</option>
                  <option value="Drive Thru">Drive Thru</option>
                  <option value="Premium">Premium</option>
                </select>
                {form.branch_type === "Premium" && (
                  <p className="text-xs text-teal-700 mt-1">{t("branch_type_premium_hint")}</p>
                )}
              </div>
              <div>
                <label className="inp-label flex items-center gap-1">
                  <UserCog size={12} /> {t("branch_admin")}
                </label>
                <select
                  className="inp"
                  value={form.admin_id}
                  onChange={(e) => setForm({ ...form, admin_id: e.target.value })}
                  data-testid="branch-admin-select"
                >
                  <option value="">— {t("no_admin_set")} —</option>
                  {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {editing.id && form.admin_id && form.admin_id !== editing.admin_id && (
                  <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <Info size={11} /> {t("propagate_info")}
                  </p>
                )}
              </div>
              {(user?.role === "super_admin" || user?.role === "area_manager") && editing.id && (
                <div className="border-t border-gray-100 pt-4" data-testid="branch-move-area-section">
                  <label className="inp-label flex items-center gap-1">
                    <ArrowLeftRight size={12} /> {t("move_branch_area_title")}
                  </label>
                  <div className="space-y-2">
                    <select className="inp" value={moveAm} onChange={(e) => { setMoveAm(e.target.value); setMoveSup(""); setMoveConfirm(false); setSaveError(""); }} data-testid="move-area-am-select">
                      <option value="">— {t("move_branch_area_pick_am")} —</option>
                      {areaManagers.filter((m) => m.id !== editing.area_manager_id).map((m) => (
                        <option key={m.id} value={m.id}>{m.full_name || m.username}</option>
                      ))}
                    </select>
                    {moveAm && (
                      <select className="inp" value={moveSup} onChange={(e) => setMoveSup(e.target.value)} data-testid="move-area-sup-select">
                        <option value="">— {t("move_branch_area_no_sup")} —</option>
                        {moveSupOptions.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    )}
                    {moveMsg && (
                      <div className={`rounded-lg p-2.5 text-sm ${moveMsg.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 border border-amber-300 text-amber-900"}`} data-testid="move-area-msg">
                        {moveMsg.text}
                      </div>
                    )}
                    {moveConfirm ? (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="move-area-confirm-panel">
                        <p className="text-sm text-amber-900">
                          {t("move_branch_area_confirm")
                            .replace("{branch}", editing.name)
                            .replace("{am}", areaManagers.find((m) => m.id === moveAm)?.full_name || areaManagers.find((m) => m.id === moveAm)?.username || "")}
                        </p>
                        <div className="flex gap-2">
                          <button className="btn btn-primary flex-1 justify-center" disabled={moveBusy} onClick={moveArea} data-testid="move-area-confirm-btn">
                            {moveBusy ? t("saving") : t("move_branch_area_confirm_btn")}
                          </button>
                          <button className="btn btn-secondary" disabled={moveBusy} onClick={() => setMoveConfirm(false)} data-testid="move-area-cancel-btn">
                            {t("move_branch_area_cancel_btn")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn btn-outline w-full justify-center" disabled={!moveAm || moveBusy} onClick={() => { setMoveMsg(null); setMoveConfirm(true); }} data-testid="move-area-btn">
                        <ArrowLeftRight size={14} /> {t("move_branch_area_btn")}
                      </button>
                    )}
                    <p className="text-xs text-gray-400">{t("move_branch_area_hint")}</p>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button className="btn btn-secondary" onClick={close}>{t("cancel")}</button>
                <button className="btn btn-primary" onClick={save} data-testid="save-branch-button">{t("save")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
